/**
 * startupRecovery — single, pure startup routing decision (1.72).
 *
 * If the WebView / app restarts while a user is working, a valid session plus a
 * valid active workflow must return them straight to that workflow — never via
 * /auth or /dashboard. Face ID is never used to re-authenticate a valid session.
 *
 * Pure decision + safe telemetry only. No photo / queue / draft logic here.
 */
import { captureEvent } from "@/lib/observability";
import { APP_VERSION, IOS_BUILD } from "@/lib/appVersion";
import type { ActiveWorkflow } from "@/lib/activeWorkflowGuard";

export const AUTH_TOKEN_STORAGE_KEY = "sb-wfsbisepapnnnqyyekip-auth-token";

export type StartupReason =
  | "bypass"
  | "active_workflow_restored"
  | "route_preserved"
  | "dashboard_fallback"
  | "crash_loop_dashboard"
  | "auth_required";

export interface StartupDecisionInput {
  /** AuthContext resolved a usable session. */
  hasSession: boolean;
  /** Refresh token persisted (session restore slow/offline). */
  hasStoredTokens: boolean;
  pathname: string;
  /** Already validated via getRecoverableWorkflow(). */
  workflow: ActiveWorkflow | null;
  bypass: boolean;
  preserveRoute: boolean;
  isAppraisalRoute: boolean;
  /** Genuine startup loop (no active workflow) detected. */
  crashLoop: boolean;
}

export interface StartupDecision {
  /** Route to navigate to, or null to stay on the current route. */
  target: string | null;
  reason: StartupReason;
}

export function decideStartupRoute(i: StartupDecisionInput): StartupDecision {
  if (i.bypass) return { target: null, reason: "bypass" };

  const signedIn = i.hasSession || i.hasStoredTokens;
  if (!signedIn) return { target: "/auth", reason: "auth_required" };

  if (i.workflow) {
    return {
      target: i.pathname === i.workflow.route ? null : i.workflow.route,
      reason: "active_workflow_restored",
    };
  }
  if (i.crashLoop) return { target: "/dashboard", reason: "crash_loop_dashboard" };
  if (i.hasSession && i.preserveRoute) return { target: null, reason: "route_preserved" };
  if (!i.hasSession && i.isAppraisalRoute) return { target: null, reason: "route_preserved" };
  return { target: "/dashboard", reason: "dashboard_fallback" };
}

/** Wrap a function so only its first call runs. */
export function once<A extends unknown[]>(fn: (...args: A) => void): ((...args: A) => void) & { called: () => boolean } {
  let done = false;
  const wrapped = ((...args: A) => {
    if (done) return;
    done = true;
    fn(...args);
  }) as ((...args: A) => void) & { called: () => boolean };
  wrapped.called = () => done;
  return wrapped;
}

export function hasStoredAuthTokens(): boolean {
  try {
    const raw = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    return !!raw && raw.includes("refresh_token");
  } catch {
    return false;
  }
}

/** User id from the persisted session (never returns tokens). */
export function getStoredAuthUserId(): string | null {
  try {
    const raw = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const id = parsed?.user?.id ?? parsed?.currentSession?.user?.id;
    return typeof id === "string" ? id : null;
  } catch {
    return null;
  }
}

export function routeCategory(pathname: string): string {
  if (pathname === "/" || pathname === "") return "root";
  if (pathname.startsWith("/resume-job/")) return "resume_job";
  if (pathname.startsWith("/appraisal/")) return "appraisal";
  if (pathname.startsWith("/job/")) return "job";
  if (pathname.startsWith("/auth")) return "auth";
  if (pathname.startsWith("/dashboard")) return "dashboard";
  return "other";
}

export type StartupEventName =
  | "startup.cold_start"
  | "startup.webview_reload"
  | "startup.session_restored"
  | "startup.active_workflow_restored"
  | "startup.face_id_skipped_valid_session"
  | "startup.face_id_required"
  | "startup.recovery_failed";

/** Safe metadata only: version, build, reason, route category. */
export function emitStartupEvent(name: StartupEventName, reason: string, pathname: string): void {
  try {
    captureEvent({
      kind: "native",
      severity: name === "startup.recovery_failed" ? "warn" : "info",
      component: "startup.recovery",
      message: name,
      route: routeCategory(pathname),
      metadata: {
        app_version: APP_VERSION,
        native_build: IOS_BUILD,
        recovery_reason: reason,
        route_category: routeCategory(pathname),
      },
    });
  } catch {
    /* telemetry never blocks startup */
  }
}
